import BubbleChart from 'components/molecules/BubbleChart';
import { colorConstants } from 'config/colorConstants';
import ButtonGroup from 'antd/lib/button/button-group';
import { Button, Dropdown, Switch } from 'antd';
import DownSvg from 'assets/svg/Down';
import CopyToClipBoard from 'components/molecules/MicroIntractions/CopyTOClipBoard';
import DownloadExcel from 'components/molecules/MicroIntractions/DownloadExcel';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { calculateContainerHeight } from 'utils/helperMethods';
import CustomDropdownButton from 'components/atoms/CustomDropdownButton';
import { UserContext } from 'contexts/ConfigContext';
import { getConfig } from 'utils/helperMethods';
import useStrategicOpportunityData from './hooks/useStrategicOpportunityData';
import Loader from 'components/molecules/Loader';
import { cloneDeep, startCase } from 'lodash';
import styled from '@emotion/styled';
import tw from 'twin.macro';
import { FilterContextData } from 'contexts/FilterContext';
import { strategicOpportunityDownload } from 'parser/graphToExcelDownloadParser';
import { trackEventMixpanel } from 'utils/mixpanel';
import {
    PP_STRATEGIC_OPPORTUNITY_ABSOLUTE_PERCENTAGE_TOGGLED,
    PP_STRATEGIC_OPPORTUNITY_COLOR_BY_CHANGED,
    PP_STRATEGIC_OPPORTUNITY_DETAILS_TOGGLED,
    PP_STRATEGIC_OPPORTUNITY_MODE_CHANGED,
    PP_STRATEGIC_OPPORTUNITY_SCATTER_DIMENSION_CHANGED,
} from 'utils/mixpanelEventNames';

const CustomButtonGroupButton = styled(Button)`
    box-shadow: none !important;
    height: 100% !important;
    display: flex;
    align-items: center;
    &:hover:not(:disabled) {
        ${tw`border-purpleFont text-headerColor`}
    }
`;

export interface StrategicOpportunityKpis {
    xaxis: {
        tier: string[];
        kpi: string;
        label: string;
    };
    yaxis: {
        tier: string[];
        kpi: string;
        label: string;
    };
    bubbleSize: {
        tier: string[];
        kpi: string;
        label?: string;
    };
    label: string;
}

const StrategicOpportunity = () => {
    const chartRef = useRef<any>(null);
    const chartHeight = calculateContainerHeight(1);
    const { appConfig } = useContext(UserContext);
    const { allDimAndAttributeTypeList } = useContext(FilterContextData);

    const dim1AttributeList = useMemo(
        () =>
            allDimAndAttributeTypeList?.dimentions[0]?.attribute.map((_item: any) => {
                return {
                    ..._item,
                    key: _item.name,
                    label: startCase(_item.name),
                    dimension: _item?.dimensionReference,
                };
            }),
        [allDimAndAttributeTypeList],
    );

    const profitPoolConfig = getConfig('profitPool', appConfig);
    const {
        defaultStrategicOpportunitySelection,
        defaultStrategicOpportunityOptions,
        defaultAttributeForStrategicOpportunityData,
        defaultAttributeForStrategicOpportunityColoredByData,
        customStrategicOpportunityXaxisOptions,
        customStrategicOpportunityYaxisOptions,
        strategicOpportunityMetricConfig,
    } = profitPoolConfig;

    const [selectedStrategicOpportunityKpis, setSelectedStrategicOpportunityKpis] = useState<StrategicOpportunityKpis>(
        defaultStrategicOpportunitySelection,
    );

    const [showChartLabel, setShowChartLabel] = useState(false);
    const [metricToggle, setMetricToggle] = useState<'abs' | 'percent'>('percent');

    const [selectedSubFilter, setSelectedSubFilter] = useState<{
        dataKey: string;
        dimension: string;
        isAttribute: boolean;
        name?: string;
    }>(defaultAttributeForStrategicOpportunityData);

    const [selectedColoredBy, setSelectedColoredBy] = useState<any>(
        defaultAttributeForStrategicOpportunityColoredByData,
    );

    const coloredByOptions = useMemo(() => {
        const removeTillIndex = dim1AttributeList?.findIndex((item: any) => item.name === selectedSubFilter.dataKey);
        trackEventMixpanel(PP_STRATEGIC_OPPORTUNITY_COLOR_BY_CHANGED, {
            color_by: removeTillIndex ? dim1AttributeList[removeTillIndex - 1]?.label : 'Not Available',
            dimension_name: removeTillIndex ? dim1AttributeList[removeTillIndex - 1]?.dimension : '',
        });
        removeTillIndex
            ? setSelectedColoredBy(dim1AttributeList[removeTillIndex - 1])
            : setSelectedColoredBy({
                  attributeType: '',
                  dimension: '',
                  isAttribute: false,
                  dataset: [],
                  key: '',
                  dataKey: '',
              });
        const slicedOptions = dim1AttributeList?.slice(0, removeTillIndex);
        return slicedOptions;
    }, [dim1AttributeList, selectedSubFilter.dataKey]);

    // Update KPIs based on metric toggle and config
    const effectiveStrategicOpportunityKpis = useMemo(() => {
        if (selectedStrategicOpportunityKpis.label === 'Custom') {
            return selectedStrategicOpportunityKpis;
        }

        const metricConfig = strategicOpportunityMetricConfig?.[selectedStrategicOpportunityKpis.label];
        if (!metricConfig) {
            return selectedStrategicOpportunityKpis;
        }

        const config = metricConfig[metricToggle];
        if (!config) {
            return selectedStrategicOpportunityKpis;
        }

        // Get the original option to preserve tier information
        const originalOption = defaultStrategicOpportunityOptions.find(
            (opt: any) => opt.label === selectedStrategicOpportunityKpis.label,
        );

        if (!originalOption) {
            return selectedStrategicOpportunityKpis;
        }

        return {
            ...selectedStrategicOpportunityKpis,
            xaxis: {
                ...originalOption.xaxis,
                kpi: config.x,
            },
            yaxis: {
                ...originalOption.yaxis,
                kpi: config.y,
            },
        };
    }, [
        selectedStrategicOpportunityKpis,
        metricToggle,
        strategicOpportunityMetricConfig,
        defaultStrategicOpportunityOptions,
    ]);

    const { xAxisData, isPending, isError, isSuccess, transformedData, allSortedTactics } = useStrategicOpportunityData(
        effectiveStrategicOpportunityKpis,
        selectedSubFilter,
        selectedColoredBy,
    );

    useEffect(() => {
        trackEventMixpanel(PP_STRATEGIC_OPPORTUNITY_MODE_CHANGED, {
            mode: selectedStrategicOpportunityKpis.label,
            xaxis: selectedStrategicOpportunityKpis.xaxis.label,
            yaxis: selectedStrategicOpportunityKpis.yaxis.label,
        });
    }, [selectedStrategicOpportunityKpis]);

    return (
        <div>
            <div
                className={`border border-borderLinePurpule bg-white p-4  rounded-lg            
            `}
            >
                {/* Prefixed Selections */}
                <div className="flex flex-row gap-x-4 items-center">
                    <p className="text-base font-semibold">{'Strategic Opportunity'}</p>
                    <ButtonGroup>
                        {defaultStrategicOpportunityOptions?.map((_option: any) => {
                            return (
                                <CustomButtonGroupButton
                                    type={
                                        selectedStrategicOpportunityKpis.label === _option.label ? 'primary' : 'default'
                                    }
                                    onClick={() => {
                                        trackEventMixpanel(PP_STRATEGIC_OPPORTUNITY_MODE_CHANGED, {
                                            mode: _option.label,
                                            xaxis: _option.xaxis.label,
                                            yaxis: _option.yaxis.label,
                                        });
                                        setSelectedStrategicOpportunityKpis(_option);
                                    }}
                                >
                                    <p>{_option.label}</p>
                                </CustomButtonGroupButton>
                            );
                        })}
                        <CustomButtonGroupButton
                            onClick={() => {
                                setMetricToggle('abs');
                                setSelectedStrategicOpportunityKpis({
                                    ...cloneDeep(selectedStrategicOpportunityKpis),
                                    label: 'Custom',
                                });
                            }}
                            type={selectedStrategicOpportunityKpis.label === 'Custom' ? 'primary' : 'default'}
                        >
                            {'Custom'}
                        </CustomButtonGroupButton>
                    </ButtonGroup>
                </div>

                {/* Selections Panel */}
                <div className="my-4 flex flex-row gap-x-2 justify-between">
                    <div className="flex">
                        <div className="flex gap-x-2 items-center">
                            <p>{'Colors by'}</p>
                            <Dropdown
                                menu={{
                                    items: coloredByOptions,
                                    onClick: (info) => {
                                        const currentSelectedColorBy = coloredByOptions.find(
                                            (item: any) => item.key === info.key,
                                        );
                                        trackEventMixpanel(PP_STRATEGIC_OPPORTUNITY_COLOR_BY_CHANGED, {
                                            color_by: currentSelectedColorBy.label,
                                            dimension_name: currentSelectedColorBy.dimension,
                                        });
                                        setSelectedColoredBy(currentSelectedColorBy);
                                    },
                                }}
                            >
                                <CustomDropdownButton>
                                    <div
                                        className={`flex flex-row gap-x-2 items-center ${
                                            !coloredByOptions?.length ? 'opacity-50' : ''
                                        }`}
                                    >
                                        <p>
                                            {coloredByOptions?.length
                                                ? startCase(selectedColoredBy?.key)
                                                : 'Not Available'}
                                        </p>
                                        <DownSvg />
                                    </div>
                                </CustomDropdownButton>
                            </Dropdown>
                        </div>
                        <div className="flex gap-x-2 items-center">
                            <p className="ml-2">{'Scattered by'}</p>
                            <Dropdown
                                menu={{
                                    items: dim1AttributeList,
                                    onClick: (info) => {
                                        const currentSelectedLevel = dim1AttributeList.find(
                                            (item: any) => item.key === info.key,
                                        ).name;
                                        trackEventMixpanel(PP_STRATEGIC_OPPORTUNITY_SCATTER_DIMENSION_CHANGED, {
                                            dimension_name: currentSelectedLevel,
                                        });
                                        setSelectedSubFilter({
                                            ...dim1AttributeList.find((item: any) => item.key === info.key),
                                            dataKey: currentSelectedLevel,
                                        });
                                    },
                                }}
                                trigger={['click']}
                                placement="bottomRight"
                            >
                                <CustomDropdownButton>
                                    <div className="flex flex-row gap-x-2 items-center">
                                        <p>{startCase(selectedSubFilter?.dataKey)}</p>
                                        <DownSvg />
                                    </div>
                                </CustomDropdownButton>
                            </Dropdown>
                        </div>
                    </div>

                    {selectedStrategicOpportunityKpis?.label === 'Custom' && (
                        <div className="flex">
                            <div className="flex justify-end items-center gap-x-2">
                                <p className="ml-2">{'X-axis'}</p>
                                <Dropdown
                                    menu={{
                                        items: customStrategicOpportunityXaxisOptions,
                                        onClick: (info) => {
                                            setSelectedStrategicOpportunityKpis({
                                                ...cloneDeep(selectedStrategicOpportunityKpis),
                                                xaxis: customStrategicOpportunityXaxisOptions.find(
                                                    (option: any) => option.key === info.key,
                                                ),
                                            });
                                        },
                                    }}
                                    trigger={['click']}
                                    placement="bottomRight"
                                >
                                    <CustomDropdownButton>
                                        <div className="flex flex-row gap-x-2 items-center">
                                            <p>{selectedStrategicOpportunityKpis.xaxis.label}</p>
                                            <DownSvg />
                                        </div>
                                    </CustomDropdownButton>
                                </Dropdown>
                            </div>
                            <div className="flex justify-end items-center gap-x-2">
                                <p className="ml-2">{'Y-axis'}</p>
                                <Dropdown
                                    menu={{
                                        items: customStrategicOpportunityYaxisOptions,
                                        onClick: (info) => {
                                            setSelectedStrategicOpportunityKpis({
                                                ...cloneDeep(selectedStrategicOpportunityKpis),
                                                yaxis: customStrategicOpportunityYaxisOptions.find(
                                                    (option: any) => option.key === info.key,
                                                ),
                                            });
                                        },
                                    }}
                                    trigger={['click']}
                                    placement="bottomRight"
                                >
                                    <CustomDropdownButton>
                                        <div className="flex flex-row gap-x-2 items-center">
                                            <p>{selectedStrategicOpportunityKpis.yaxis.label}</p>
                                            <DownSvg />
                                        </div>
                                    </CustomDropdownButton>
                                </Dropdown>
                            </div>
                        </div>
                    )}
                </div>

                {/* Chart Section */}
                <div className="mt-4 flex flex-col border border-[#0000000F] rounded-lg px-4 pt-4 w-full">
                    {/* Title & Section Controls */}

                    {isPending ? (
                        <div>
                            <p className="text-lg font-semibold">{`Strategic Opportunities Matrix - ${startCase(
                                selectedSubFilter.dataKey,
                            )}`}</p>
                            <div
                                style={{
                                    height: chartHeight - 200,
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                }}
                            >
                                <Loader />
                            </div>
                        </div>
                    ) : (
                        <>
                            <div className="flex flex-row items-center justify-between">
                                <p className="text-lg font-semibold">{`Strategic Opportunities Matrix - ${startCase(
                                    selectedSubFilter.dataKey,
                                )}`}</p>
                                <div className="flex flex-row gap-x-2 items-center">
                                    {selectedStrategicOpportunityKpis.label !== 'Custom' && (
                                        <div className="flex gap-2 items-center mr-[3px]">
                                            <span className="font-xs">Abs</span>
                                            <Switch
                                                style={{
                                                    backgroundColor:
                                                        metricToggle === 'percent'
                                                            ? colorConstants.violet
                                                            : colorConstants.borderGrey,
                                                }}
                                                defaultChecked={true}
                                                onChange={(checked) => {
                                                    trackEventMixpanel(
                                                        PP_STRATEGIC_OPPORTUNITY_ABSOLUTE_PERCENTAGE_TOGGLED,
                                                        {
                                                            selected_mode: checked ? 'percent' : 'abs',
                                                        },
                                                    );
                                                    setMetricToggle(checked ? 'percent' : 'abs');
                                                }}
                                                checked={metricToggle === 'percent'}
                                            />
                                            <span className="font-xs">%</span>
                                        </div>
                                    )}
                                    <div className="flex gap-2 items-center mr-[3px]">
                                        <span className="font-xs">Details</span>
                                        <Switch
                                            style={{
                                                backgroundColor: showChartLabel
                                                    ? colorConstants.violet
                                                    : colorConstants.borderGrey,
                                            }}
                                            defaultChecked={false}
                                            onChange={() => {
                                                trackEventMixpanel(PP_STRATEGIC_OPPORTUNITY_DETAILS_TOGGLED, {
                                                    show_labels: !showChartLabel,
                                                });
                                                setShowChartLabel(!showChartLabel);
                                            }}
                                            checked={showChartLabel}
                                        />
                                    </div>
                                    <div className="flex justify-end items-center">
                                        <span className="flex gap-2">
                                            <CopyToClipBoard refOfComponent={chartRef} />
                                            <DownloadExcel
                                                excelData={strategicOpportunityDownload(
                                                    transformedData,
                                                    effectiveStrategicOpportunityKpis.xaxis.label,
                                                    effectiveStrategicOpportunityKpis.yaxis.label,
                                                    startCase(selectedColoredBy?.key || ''),
                                                )}
                                                headers={[
                                                    'Attribute Ex ID',
                                                    'Attribute Desc',
                                                    effectiveStrategicOpportunityKpis.xaxis.label,
                                                    effectiveStrategicOpportunityKpis.yaxis.label,
                                                    effectiveStrategicOpportunityKpis.bubbleSize.label || 'Bubble Size',
                                                    `Bubble Color (${startCase(selectedColoredBy?.key || '')})`,
                                                ]}
                                                title={'StrategicOpportunity'}
                                            />
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {/* Chart */}
                            <div ref={chartRef}>
                                <BubbleChart
                                    chartContainer={'bubble-opportunity-identificatin'}
                                    height={window.innerHeight * (2.2 / 3)}
                                    dataset={transformedData}
                                    k={showChartLabel}
                                    labelConfiguration={showChartLabel}
                                    xAsisTitle={effectiveStrategicOpportunityKpis.xaxis.label}
                                    yAxisTitle={effectiveStrategicOpportunityKpis.yaxis.label}
                                    bubbleSizeTitle={effectiveStrategicOpportunityKpis.bubbleSize.label}
                                    showQuadrant={true}
                                    // [xaxis, yaxis, colorField, size(also used for tooltip title), duplicateForSize, label]
                                    fields={[
                                        'xFieldValue',
                                        'yFieldValue',
                                        'tactics',
                                        'bubbleSizeValue',
                                        'bubbleSizeValue',
                                        'name',
                                    ]}
                                    alter={false}
                                    lineColor={colorConstants.borderGrey}
                                    fill={'#171E3E'}
                                    stroke={'#d9d9d9'}
                                    loading={false}
                                    error={null}
                                    colorFieldOrder={allSortedTactics}
                                    xAxisPercentage={
                                        metricToggle === 'percent' ||
                                        effectiveStrategicOpportunityKpis.xaxis.label.includes('%')
                                    }
                                    yAxisPercentage={
                                        metricToggle === 'percent' ||
                                        effectiveStrategicOpportunityKpis.yaxis.label.includes('%')
                                    }
                                />
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default StrategicOpportunity;
